import { useMemo } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { axios } from '#api/axios';

import { PrevHeaderBarWithTitleTemplate } from '#templates';

import { debounce } from 'lodash';
import { useToast } from '#providers/ToastProvider';
import { AxiosError } from 'axios';
import { useNavigate } from 'react-router';
import { Link } from 'react-router-dom';

const GymEdit = () => {
  const methods = useForm();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  const getGymList = async (name: string) => {
    const res = await axios.get<
      Array<{ name: string; address: string; id: string }>
    >('/gyms/list', {
      params: {
        name
      }
    });

    return res.data;
  };

  const { data } = useQuery({
    queryKey: ['gym-list', methods.watch('gymName')],
    queryFn: () => getGymList(methods.watch('gymName')),
    enabled: !!methods.watch('gymName')
  });

  const gymNameDebounce = useMemo(() => {
    return debounce(e => {
      methods.setValue('gymName', e.target.value);
    }, 500);
  }, []);

  const { mutate } = useMutation({
    mutationFn: (payload: string) => axios.post(`gyms/${payload}/v2`),
    onSuccess: () => {
      showToast({
        message: '수업 공간을 추가했습니다.'
      });
      queryClient.invalidateQueries({ queryKey: ['get-coach-gym-list'] });
      queryClient.invalidateQueries({ queryKey: ['gym-list'] });
      return navigate('/profile/gym');
    },
    onError: (error: AxiosError<{ message: string }>) =>
      showToast({
        type: 'fail',
        message: error?.response?.data.message
          ? error?.response?.data.message
          : '일시적인 에러입니다. 다시 시도해주세요.'
      })
  });

  return (
    <PrevHeaderBarWithTitleTemplate title="수업 장소 추가" defaultBackground>
      <FormProvider {...methods}>
        <div className="p-20 h-full flex flex-col gap-12">
          <input
            {...(methods.register('gymName'),
            {
              onChange: gymNameDebounce
            })}
            className="focus:bg-white focus:text-black w-full bg-transparent border border-[#494949] py-[16.5px] px-10 text-16 rounded-[5px] placeholder:text-[#999]"
            placeholder="지점 및 센터명으로 검색"
          />
          {data ? (
            <div className="py-8 flex justify-between items-center">
              <p className="text-[#9170F2]">{data.length} 개</p>
              <button
                onClick={() => mutate(methods.getValues('gymId'))}
                className=" text-center py-4 px-18 text-[14px] rounded-8 bg-[#9170F2] mt-auto disabled:bg-[#494949]"
                disabled={!methods.watch('gymId')}
              >
                추가
              </button>
            </div>
          ) : null}
          <div className="flex flex-col gap-20 mb-20 overflow-y-scroll">
            {data &&
              data.map(value => {
                return (
                  <div className="border-solid border-b-1 border-[#24222A] pb-20">
                    <input
                      {...methods.register('gymId')}
                      type="radio"
                      className="hidden peer"
                      key={value.id}
                      id={`gym-${value.id}`}
                      value={value.id}
                    />
                    <label
                      className="peer-checked:text-[#9170F2] peer-checked:font-bold flex flex-col gap-4 "
                      htmlFor={`gym-${value.id}`}
                    >
                      <p>{value.name}</p>
                      <p className="text-[14px] text-[#8E899F]">
                        {value.address}
                      </p>
                    </label>
                  </div>
                );
              })}
          </div>
          <div className="mt-auto flex flex-col items-center justify-center gap-24 w-full text-center text-[#8E899F]">
            <p>
              원하는 검색 결과가 없으신가요? <br />
              장소를 직접 등록해 보세요{' '}
            </p>
            <Link
              to={'/profile/gym/register'}
              className="py-[11.5px] rounded-8 w-full bg-[#9170F2] text-[#EAE8ED]"
            >
              등록하기
            </Link>
          </div>
        </div>
      </FormProvider>
    </PrevHeaderBarWithTitleTemplate>
  );
};

export default GymEdit;
